/**
 * Entrada.jsx — Registro de Entradas de Estoque
 * Aceita: produtos (tipo='produto') e alugáveis (tipo='alugavel')
 * Exclui: serviços (não movimentam estoque)
 */

import { useEffect, useState, useCallback, useMemo, memo } from "react";
import api from "../../api/api";
import Swal from "sweetalert2";
import "./Entrada.scss";

// ─── Constantes ───────────────────────────────────────────────────────────────

const FORM_EMPTY = {
  id: null,
  produto_id: "",
  quantidade: "",
  preco_custo: "",
  data_validade: "",
  observacao: "",
};

// ─── Utilitários ──────────────────────────────────────────────────────────────

const formatCurrency = (value) =>
  Number(value ?? 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const formatDateTime = (isoStr) => {
  if (!isoStr) return "-";
  return new Date(isoStr).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("pt-BR");
};

// Label do tipo do produto no select
const TIPO_SUFFIX = {
  produto:  "",
  alugavel: " 🏷️ (Alugável)",
  // servico nunca aparece aqui
};

// ─── Sub-componentes ──────────────────────────────────────────────────────────

const SkeletonRow = () => (
  <tr className="entrada-table__skeleton-row" aria-hidden="true">
    {Array.from({ length: 9 }).map((_, i) => (
      <td key={i}><div className="skeleton skeleton--text" /></td>
    ))}
  </tr>
);

const EmptyState = () => (
  <tr>
    <td colSpan={9}>
      <div className="empty-state" role="status">
        <svg className="empty-state__icon" width="40" height="40" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="7.5 4.21 12 6.81 16.5 4.21" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>
        <p className="empty-state__text">Nenhuma entrada encontrada para os filtros aplicados.</p>
      </div>
    </td>
  </tr>
);

const EntradaRow = memo(({ entrada, onEdit }) => (
  <tr>
    <td data-label="Data entrada" className="cell--date">{formatDateTime(entrada.data_entrada)}</td>
    <td data-label="Produto" className="cell--produto">
      {entrada.produto_nome}
      {entrada.produto_tipo === "alugavel" && (
        <span className="tipo-pill tipo-pill--alugavel">Alugável</span>
      )}
    </td>
    <td data-label="EAN" className="cell--secondary">{entrada.ean || "-"}</td>
    <td data-label="Usuário" className="cell--secondary">{entrada.usuario_nome}</td>
    <td data-label="Quantidade"><span className="qty-badge">{entrada.quantidade}</span></td>
    <td data-label="Preço custo" className="cell--price">{formatCurrency(entrada.preco_custo)}</td>
    <td data-label="Validade" className="cell--secondary">{formatDate(entrada.data_validade)}</td>
    <td data-label="Observação" className="cell--obs">{entrada.observacao || "-"}</td>
    <td data-label="Ações" className="cell--actions">
      <button
        className="btn-edit btn--sm"
        onClick={() => onEdit(entrada)}
        aria-label={`Editar entrada de ${entrada.produto_nome}`}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
        Editar
      </button>
    </td>
  </tr>
));
EntradaRow.displayName = "EntradaRow";

// ─── Modal ────────────────────────────────────────────────────────────────────

const EntradaModal = memo(({ form, produtos, onClose, onSubmit, onChange }) => {
  const isEditing = Boolean(form.id);

  // ✅ Aceita 'produto' e 'alugavel' — exclui apenas 'servico'
  // Também inclui registros antigos sem campo tipo (compatibilidade)
  const produtosDisponiveis = produtos
    .filter((p) => p.ativo && p.tipo !== "servico")
    .sort((a, b) => {
      // Agrupa: produtos primeiro, alugáveis depois
      if (a.tipo === b.tipo) return a.nome.localeCompare(b.nome);
      return a.tipo === "alugavel" ? 1 : -1;
    });

  const temProdutos  = produtosDisponiveis.some((p) => p.tipo !== "alugavel");
  const temAlugaveis = produtosDisponiveis.some((p) => p.tipo === "alugavel");

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal entrada-modal">
        <div className="modal__header">
          <h3 className="modal-title" id="modal-title">
            {isEditing ? "Editar entrada" : "Nova entrada"}
          </h3>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Fechar modal">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form onSubmit={onSubmit} noValidate>
          <fieldset className="modal__fieldset">
            <legend className="modal__legend">Informações do produto</legend>

            <div className="modal__field">
              <label htmlFor="produto_id" className="modal__label">
                Produto <span aria-hidden="true" className="required">*</span>
              </label>

              {isEditing ? (
                <div className="modal__input modal__input--readonly">
                  {form.produto_nome
                    || produtosDisponiveis.find((p) => p.id === Number(form.produto_id))?.nome
                    || "—"}
                </div>
              ) : (
                <>
                  <select
                    id="produto_id"
                    className="modal__input"
                    value={form.produto_id}
                    onChange={(e) => onChange("produto_id", e.target.value)}
                    required
                  >
                    <option value="">Selecione um produto</option>

                    {/* Produtos normais agrupados */}
                    {temProdutos && (
                      <optgroup label="── Produtos ──">
                        {produtosDisponiveis
                          .filter((p) => p.tipo !== "alugavel")
                          .map((p) => (
                            <option key={p.id} value={p.id}>{p.nome}</option>
                          ))}
                      </optgroup>
                    )}

                    {/* Alugáveis agrupados */}
                    {temAlugaveis && (
                      <optgroup label="── Alugáveis ──">
                        {produtosDisponiveis
                          .filter((p) => p.tipo === "alugavel")
                          .map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.nome} 🏷️
                            </option>
                          ))}
                      </optgroup>
                    )}
                  </select>

                  {produtosDisponiveis.length === 0 && (
                    <span className="modal__hint">
                      Nenhum produto ativo encontrado. Cadastre na tela de Produtos.
                    </span>
                  )}
                </>
              )}
            </div>

            <div className="modal__row">
              <div className="modal__field">
                <label htmlFor="quantidade" className="modal__label">
                  Quantidade <span aria-hidden="true" className="required">*</span>
                </label>
                <input
                  id="quantidade"
                  type="number"
                  className="modal__input"
                  placeholder="0"
                  value={form.quantidade}
                  onChange={(e) => onChange("quantidade", Number(e.target.value))}
                  required
                  min={1}
                />
              </div>

              <div className="modal__field">
                <label htmlFor="preco_custo" className="modal__label">
                  Preço de custo <span aria-hidden="true" className="required">*</span>
                </label>
                <input
                  id="preco_custo"
                  type="number"
                  className="modal__input"
                  placeholder="0,00"
                  value={form.preco_custo}
                  onChange={(e) => onChange("preco_custo", Number(e.target.value))}
                  required
                  min={0}
                  step="0.01"
                />
              </div>
            </div>

            <div className="modal__field">
              <label htmlFor="data_validade" className="modal__label">Data de validade</label>
              <input
                id="data_validade"
                type="date"
                className="modal__input"
                value={form.data_validade || ""}
                onChange={(e) => onChange("data_validade", e.target.value)}
              />
            </div>

            <div className="modal__field">
              <label htmlFor="observacao" className="modal__label">Observação</label>
              <input
                id="observacao"
                type="text"
                className="modal__input"
                placeholder="Ex: Lote A, fornecedor X…"
                value={form.observacao}
                onChange={(e) => onChange("observacao", e.target.value)}
              />
            </div>
          </fieldset>

          <div className="modal-buttons">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn-save">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {isEditing ? "Salvar alterações" : "Registrar entrada"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});
EntradaModal.displayName = "EntradaModal";

// ─── Componente principal ─────────────────────────────────────────────────────

const Entrada = () => {
  const [entradas, setEntradas]   = useState([]);
  const [produtos, setProdutos]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm]           = useState(FORM_EMPTY);

  const [busca, setBusca]           = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim]       = useState("");

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchEntradas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/entrada");
      setEntradas(res.data);
    } catch (err) {
      console.error("[Entrada] Erro ao carregar entradas:", err);
      Swal.fire("Erro", "Não foi possível carregar as entradas.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProdutos = useCallback(async () => {
    try {
      // Busca todos — o filtro de serviço é feito no componente
      const res = await api.get("/produtos");
      setProdutos(res.data);
    } catch (err) {
      console.error("[Entrada] Erro ao carregar produtos:", err);
    }
  }, []);

  useEffect(() => {
    fetchEntradas();
    fetchProdutos();
  }, [fetchEntradas, fetchProdutos]);

  // ── Modal ──────────────────────────────────────────────────────────────────

  const openModal = useCallback((entrada = null) => {
    setForm(entrada ? { ...entrada } : FORM_EMPTY);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setForm(FORM_EMPTY);
  }, []);

  const handleFieldChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      try {
        if (form.id) {
          await api.put(`/entrada/${form.id}`, form);
          Swal.fire({ title: "Atualizada!", icon: "success", timer: 1800, showConfirmButton: false });
        } else {
          await api.post("/entrada", form);
          Swal.fire({ title: "Registrada!", icon: "success", timer: 1800, showConfirmButton: false });
        }
        fetchEntradas();
        closeModal();
      } catch (err) {
        console.error("[Entrada] Erro ao salvar:", err);
        const msg = err.response?.data?.error || "Não foi possível salvar a entrada.";
        Swal.fire("Erro", msg, "error");
      }
    },
    [form, fetchEntradas, closeModal]
  );

  // ── Filtro ─────────────────────────────────────────────────────────────────

  const filtradas = useMemo(() => {
    const termo = busca.toLowerCase();
    return entradas.filter((e) => {
      const matchBusca =
        !termo ||
        e.produto_nome?.toLowerCase().includes(termo) ||
        e.usuario_nome?.toLowerCase().includes(termo);

      const dataEntrada = new Date(e.data_entrada);
      const matchInicio = !dataInicio || dataEntrada >= new Date(dataInicio);
      const matchFim    = !dataFim    || dataEntrada <= new Date(dataFim + "T23:59:59");

      return matchBusca && matchInicio && matchFim;
    });
  }, [entradas, busca, dataInicio, dataFim]);

  // ── Totais ─────────────────────────────────────────────────────────────────

  const totalVolumes = useMemo(
    () => filtradas.reduce((acc, e) => acc + Number(e.quantidade), 0),
    [filtradas]
  );

  const totalValor = useMemo(
    () => filtradas.reduce((acc, e) => acc + Number(e.preco_custo) * Number(e.quantidade), 0),
    [filtradas]
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="entrada-page">

      {/* ── Cabeçalho ── */}
      <div className="entrada-page__header">
        <div className="entrada-page__header-text">
          <h1 className="entrada-page__title">Entradas de Estoque</h1>
          <p className="entrada-page__subtitle">Registro de entradas de produtos e itens alugáveis</p>
        </div>
        <button className="btn-add" onClick={() => openModal()} aria-label="Adicionar nova entrada">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Adicionar entrada
        </button>
      </div>

      {/* ── Cards resumo ── */}
      <div className="entrada-summary">
        <div className="summary-card">
          <p className="summary-card__label">Registros</p>
          <p className="summary-card__value">{filtradas.length}</p>
        </div>
        <div className="summary-card">
          <p className="summary-card__label">Volumes</p>
          <p className="summary-card__value">{totalVolumes.toLocaleString("pt-BR")}</p>
        </div>
        <div className="summary-card summary-card--accent">
          <p className="summary-card__label">Valor total</p>
          <p className="summary-card__value">{formatCurrency(totalValor)}</p>
        </div>
      </div>

      {/* ── Filtros + tabela ── */}
      <div className="entrada-card">
        <div className="entrada-filters" role="search" aria-label="Filtros de entrada">
          <div className="filter-search">
            <svg className="filter-search__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="search"
              className="filter-search__input"
              placeholder="Buscar por produto ou usuário…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              aria-label="Buscar entradas"
            />
          </div>

          <div className="filter-dates">
            <div className="filter-date-group">
              <label htmlFor="dataInicio" className="filter-date-group__label">De</label>
              <input id="dataInicio" type="date" className="filter-date-group__input"
                value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
            </div>
            <div className="filter-date-group">
              <label htmlFor="dataFim" className="filter-date-group__label">Até</label>
              <input id="dataFim" type="date" className="filter-date-group__input"
                value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
            </div>
          </div>

          {filtradas.length > 0 && (
            <span className="filter-count" aria-live="polite">
              {filtradas.length} registro{filtradas.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        <div className="entrada-table-wrapper" role="region" aria-label="Tabela de entradas">
          <table className="entrada-table">
            <thead>
              <tr>
                <th scope="col">Data entrada</th>
                <th scope="col">Produto</th>
                <th scope="col">EAN</th>
                <th scope="col">Usuário</th>
                <th scope="col">Qtd</th>
                <th scope="col">Preço custo</th>
                <th scope="col">Validade</th>
                <th scope="col">Observação</th>
                <th scope="col">Ações</th>
              </tr>
            </thead>
            <tbody aria-live="polite" aria-busy={loading}>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
              ) : filtradas.length === 0 ? (
                <EmptyState />
              ) : (
                filtradas.map((e) => (
                  <EntradaRow key={e.id} entrada={e} onEdit={openModal} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modal ── */}
      {modalOpen && (
        <EntradaModal
          form={form}
          produtos={produtos}
          onClose={closeModal}
          onSubmit={handleSubmit}
          onChange={handleFieldChange}
        />
      )}
    </div>
  );
};

export default Entrada;